import React, { useEffect } from 'react';
import { useQuery, useMutation, useLazyQuery } from 'react-apollo';
import IS_SIGNUP_V2_MIGRATION_NEEDED from '../../../../GraphQl/Queries/IS_SIGNUP_V2_MIGRATION_NEEDED';
import GET_SIGNUPS_V2_BY_DATE from '../../../../GraphQl/Queries/GET_SIGNUPS_V2_BY_DATE';
import GET_SIGNUPS_V2_WITH_EVENTS_PAGINATED from '../../../../GraphQl/Queries/GET_SIGNUPS_V2_WITH_EVENTS_PAGINATED';
import MIGRATE_SIGNUPS_TO_PRISMA from '../../../../GraphQl/Queries/MIGRATE_SIGNUPS_TO_PRISMA';
import Controller from './SignUpsV2Controller';
import OverlaySpinner from '../../../../../platform/shared/OverlaySpinner';

const SignUpsV2DataLoader = ({ children, campaignOrder, ...props }) => {
  const campaignOrderId = campaignOrder.id;

  // Check if migration is needed
  const {
    loading: migrationCheckLoading,
    data: migrationCheckData,
    refetch: refetchMigrationStatus,
  } = useQuery(IS_SIGNUP_V2_MIGRATION_NEEDED, {
    variables: { campaignOrderId },
    fetchPolicy: 'cache-first',
  });

  const isSignupMigrationNeeded = migrationCheckData?.isSignupMigrationNeeded !== false;

  // Lazy query for signups by date (for chart)
  const [getSignupsByDate, { loading: signupsByDateLoading, data: signupsByDateData }] =
    useLazyQuery(GET_SIGNUPS_V2_BY_DATE, {
      fetchPolicy: 'cache-first',
    });

  // Lazy query for paginated signups
  const [getSignupsPaginated, { loading: signupsLoading, data: signupsData }] = useLazyQuery(
    GET_SIGNUPS_V2_WITH_EVENTS_PAGINATED,
    {
      fetchPolicy: 'no-cache',
    }
  );

  // Mutation for migration (supports force flag for fresh migrations)
  const [migrateSignups, { loading: migrationLoading }] = useMutation(MIGRATE_SIGNUPS_TO_PRISMA, {
    onCompleted: () => {
      // Refetch migration status to update the UI
      refetchMigrationStatus();
    },
    onError: (err) => {
      console.error('Migration error:', err);
    },
  });

  // Auto-fetch signups by date when migration is complete
  useEffect(() => {
    if (!isSignupMigrationNeeded) {
      getSignupsByDate({
        variables: { campaignOrderId },
      });
    }
  }, [isSignupMigrationNeeded, campaignOrderId, getSignupsByDate]);

  // Auto-fetch initial signups when migration is complete
  useEffect(() => {
    if (!isSignupMigrationNeeded) {
      getSignupsPaginated({
        variables: {
          campaignOrderId,
          skip: 0,
          first: 10,
          attrWindow: 30,
        },
      });
    }
  }, [isSignupMigrationNeeded, campaignOrderId, getSignupsPaginated]);

  if (migrationCheckLoading) {
    return <OverlaySpinner />;
  }

  return (
    <Controller
      campaignOrderId={campaignOrderId}
      campaignOrder={campaignOrder}
      isSignupMigrationNeeded={isSignupMigrationNeeded}
      migrationLoading={migrationLoading}
      migrateSignups={migrateSignups}
      signupsData={signupsData?.getSignupsV2WithEventsPaginated}
      signupsLoading={signupsLoading}
      signupsByDateData={signupsByDateData?.getSignupsV2ByDate}
      chartLoading={signupsByDateLoading}
      getSignupsPaginated={getSignupsPaginated}
      getSignupsByDate={getSignupsByDate}
      refetchMigrationStatus={refetchMigrationStatus}
      {...props}
    >
      {children}
    </Controller>
  );
};

export default SignUpsV2DataLoader;
